// eslint-disable @typescript-eslint/no-explicit-any
import { Space, Typography, Upload } from 'antd';
import { RcFile, UploadProps } from 'antd/lib/upload';
import React, { FC } from 'react';
import styled from 'styled-components';
import { LinkButton } from 'components/atoms/button';
import { Caption01 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { toastService } from '../toast';

const maxImageSize = 2; // in  Mb
const defaultAccepted = 'image/png, image/jpeg, image/jpg';

const { Text } = Typography;

const HelpText = styled(Text)`
  ${Caption01};
  margin-bottom: 0px;
  color: var(--gray-60);
`;

export interface UploadTrigger extends UploadProps<any> {
  buttonText?: string;
  help?: string;
  maxSizeInMB?: number;
  isDisabled?: boolean;
  isLoading?: boolean;
  showSizeRestriction?: boolean;
  showFormatRestriction?: boolean;
  onUpload: (data: any) => void;
  beforeUpload?: (
    file: RcFile,
    FileList: RcFile[],
  ) => boolean | Promise<void | Blob | File>;
}

export const UploadTrigger: FC<UploadTrigger> = ({
  isDisabled = false,
  isLoading = false,
  showSizeRestriction = false,
  showFormatRestriction = false,
  buttonText,
  help,
  maxSizeInMB,
  onUpload,
  accept,
  beforeUpload,
}) => {
  const defaultButton = useTranslation('upload');
  const maxSize = useTranslation('max_size', {
    size: `${maxImageSize ?? maxImageSize}MB`,
  });

  // const validFormats = useTranslation('valid_formats', {
  //   values: (accept ?? defaultAccepted)
  //     ?.replaceAll('image/', '')
  //     .split(',')
  //     .map((e) => e.trim())
  //     .filter(Boolean)
  //     .join(','),
  // });
  const sizeError = useTranslation('project.settings.image.size.error', {
    size: maxSizeInMB ?? maxImageSize,
  });
  const errorTitle = useTranslation('avatar.upload.error.title');
  const typeError = useTranslation('project.settings.image.type.error');
  const uploadingtext = useTranslation('uploading');

  const defaultBeforeUpload = (file: RcFile) => {
    const isValidSize =
      file.size / (1024 * 1024) < (maxSizeInMB ?? maxImageSize);
    const isJpgOrPng = (accept ?? defaultAccepted)
      .split(',')
      .map((e) => e.trim())
      .includes(file.type);

    if (!isJpgOrPng) {
      toastService({
        title: errorTitle,
        message: typeError,
      }).error();

      return false;
    }
    if (!isValidSize) {
      toastService({
        title: errorTitle,
        message: sizeError,
      }).error();

      return false;
    }

    return true;
  };

  return (
    <Space direction="vertical" size={0}>
      <Upload
        accept={accept ?? defaultAccepted}
        customRequest={onUpload}
        showUploadList={false}
        beforeUpload={beforeUpload ?? defaultBeforeUpload}
        disabled={isDisabled || isLoading}
      >
        <LinkButton disabled={isDisabled} loading={isLoading}>
          {isLoading ? uploadingtext : buttonText ?? defaultButton}
        </LinkButton>
      </Upload>

      {/* Help */}
      {help ? <HelpText>{help}</HelpText> : null}
      <Space>
        {showSizeRestriction ? <HelpText>{maxSize}</HelpText> : null}
        {showSizeRestriction && showFormatRestriction ? '‧' : null}
        {/* {showFormatRestriction ? <HelpText>{validFormats}</HelpText> : null} */}
      </Space>
    </Space>
  );
};
